/**
 * BAU CROSSELL — bau-crossell.js v11.0
 *
 * v9.0 — Dropdown riscritto con la stessa gerarchia del BauBundle:
 *   immagine colore + nome + check (o taglie annidate sotto il
 *   colore attivo). Click sulla riga colore = seleziona la prima
 *   taglia disponibile e chiude. Click su una taglia = selezione
 *   esatta e chiude.
 *
 *   FIX: il cambio-variante quando il prodotto è già nel carrello
 *   ora esegue davvero remove+add (in v8 restava "appeso" e
 *   rimuoveva il prodotto senza più riaggiungerlo).
 *
 * v10.0:
 *   Il cross-sell riceve entrambe le famiglie di guinzaglio e decide
 *   in tempo reale quale mostrare leggendo SEMPRE la Pettorina nel
 *   carrello. Non aspetta più l'aggiunta per correggere il prodotto.
 */
(function () {
  'use strict';

  /* ─── STATO ─── */
  var S = {
    products:     [],
    selections:   {},   // pid -> { color, size }
    inCart:       {},
    cartItems:    [],
    openDropdown: null,
    pendingPid:   null,
    currency:     'EUR',
    locale:       'it-IT',
    config:       null,
    catalog:      null,
    selectionSources: {},
    dogNames: {},
    nameErrors: {}
  };

  /* ─── UTILITY ─── */
  function fmt(cents) {
    try {
      return new Intl.NumberFormat(S.locale, {
        style: 'currency', currency: S.currency,
        minimumFractionDigits: 2, maximumFractionDigits: 2
      }).format(cents / 100);
    } catch (e) { return (cents / 100).toFixed(2) + '\u00a0' + S.currency; }
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;')
      .replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  function norm(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .trim()
      .replace(/[àáâãäå]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u');
  }

  function canonicalColor(value) {
    var v = norm(value);
    if (/sakura|pink|rosa|blush/.test(v)) return 'rosa';
    if (/midnight|grey|gray|grigio/.test(v)) return 'grigio';
    if (/crimson|red|rosso/.test(v)) return 'rosso';
    if (/lucky|green|verde/.test(v)) return 'verde';
    if (/solar|orange|arancio/.test(v)) return 'arancio';
    return v;
  }

  function requiresDogName(product) {
    return !!product && (product.handle === 'pettorina-personalizzata-cane' || product.handle === 'bauidentity-patch');
  }

  function cartDogName(item) {
    var props = item && item.properties || {};
    return String(props['Nome del Cane'] || props['Nome cane'] || '').trim().slice(0, 16);
  }

  function itemOption(item, index) {
    if (!item) return '';
    if (Array.isArray(item.variant_options) && item.variant_options[index] != null) {
      return item.variant_options[index];
    }
    if (Array.isArray(item.options_with_values) && item.options_with_values[index]) {
      return item.options_with_values[index].value || '';
    }
    return item['option' + (index + 1)] || '';
  }

  function isLargeHarnessSize(size) {
    var value = norm(size).replace(/\s+/g, '');
    if (!value) return false;
    if (['l', 'xl', 'xxl', 'xxxl'].indexOf(value) !== -1) return true;
    return /(^|[^a-z])(xxxl|xxl|xl|l)($|[^a-z])/.test(norm(size));
  }

  function isDefaultTitle(product) {
    return !product.options || (
      product.options.length === 1 &&
      product.options[0].name.toLowerCase() === 'title' &&
      product.variants.length === 1
    );
  }

  /* ─── COLORI / TAGLIE (gerarchia stile bundle) ─── */
  function getColors(product) {
    var map = new Map();
    product.variants.forEach(function (v) {
      if (v.option1 && !map.has(v.option1)) {
        map.set(v.option1, { name: v.option1, image: v.image || product.image });
      }
    });
    return Array.from(map.values());
  }

  function getSizesForColor(product, color) {
    return product.variants
      .filter(function (v) {
        return v.option1 && v.option1.toLowerCase().trim() === (color || '').toLowerCase().trim();
      })
      .map(function (v) {
        return {
          size: v.option2, price: v.price, compareAtPrice: v.compare_at_price,
          variantId: v.id, available: v.available
        };
      });
  }

  function findVariant(product, color, size) {
    var matches = product.variants.filter(function (v) {
      var cMatch = !color || (v.option1 && v.option1.toLowerCase().trim() === color.toLowerCase().trim());
      var sMatch = !size  || (v.option2 && v.option2.toLowerCase().trim() === size.toLowerCase().trim());
      return cMatch && sMatch;
    });
    var avail = matches.find(function (v) { return v.available; });
    return avail || matches[0] || null;
  }

  function getDefaultVariant(product) {
    return product.variants.find(function (v) { return v.available; }) || product.variants[0] || null;
  }

  function selectedVariant(product) {
    var sel = S.selections[product.id];
    if (!sel) return getDefaultVariant(product);
    return findVariant(product, sel.color, sel.size) || getDefaultVariant(product);
  }

  function initSelectionsForProduct(product, presetColor, presetSize, sourceSignature) {
    var pid = String(product.id);
    var signature = sourceSignature || 'default';

    if (S.selections[product.id] && S.selectionSources[pid] === signature) return;

    var chosen = null;
    if (presetColor || presetSize) {
      chosen = (product.variants || []).find(function (variant) {
        var colorMatch = !presetColor || canonicalColor(variant.option1) === canonicalColor(presetColor);
        var sizeMatch  = !presetSize || norm(variant.option2) === norm(presetSize);
        return variant.available && colorMatch && sizeMatch;
      }) || null;

      if (!chosen && presetColor) {
        chosen = (product.variants || []).find(function (variant) {
          return variant.available && canonicalColor(variant.option1) === canonicalColor(presetColor);
        }) || null;
      }
    }
    if (!chosen) chosen = getDefaultVariant(product);

    S.selections[product.id] = chosen
      ? { color: chosen.option1 || '', size: chosen.option2 || '' }
      : { color: '', size: '' };

    S.selectionSources[pid] = signature;
  }

  function isInCart(variantId) { return !!S.inCart[variantId]; }

  function syncCart(cart) {
    S.inCart    = {};
    S.cartItems = cart.items || [];
    S.cartItems.forEach(function (i) { S.inCart[i.variant_id] = true; });
  }

  function getLineKey(variantId) {
    var item = S.cartItems.find(function (i) { return i.variant_id === variantId; });
    return item ? item.key : null;
  }

  function productById(pid) {
    return S.products.find(function (p) { return String(p.id) === String(pid); });
  }

  /* ─── RISOLUZIONE DINAMICA PRODOTTI DA CARRELLO ─── */
  function cloneForCrossell(product, duplicateMode) {
    if (!product) return null;
    var copy = Object.assign({}, product);
    copy._crossellDuplicate = !!duplicateMode;
    return copy;
  }

  function resolveProductsFromCart(cart) {
    var data = S.config || {};
    var catalog = data.catalog || {};
    var handles = data.handles || {};
    var items = (cart && cart.items) || S.cartItems || [];

    var harnessHandle = handles.pettorina || 'pettorina-personalizzata-cane';
    var leashXsmHandle = handles.leashXsm || 'guinzaglio-riflettente-per-cani';
    var leashLxxlHandle = handles.leashLxxl || 'guinzaglio-baugrip';
    var pouchHandles = handles.portabustine || [
      'portabustine-led-per-cani',
      'portabustine-led-luminoso-gratuito'
    ];

    var harnessItem = items.find(function (item) { return item.handle === harnessHandle; }) || null;
    var leashItem = items.find(function (item) {
      return item.handle === leashXsmHandle || item.handle === leashLxxlHandle;
    }) || null;
    var pouchItem = items.find(function (item) { return pouchHandles.indexOf(item.handle) !== -1; }) || null;

    var harnessSize = harnessItem ? itemOption(harnessItem, 1) : '';
    var sourceColor = harnessItem ? itemOption(harnessItem, 0) : (leashItem ? itemOption(leashItem, 0) : '');
    var sourceSize  = harnessItem ? itemOption(harnessItem, 1) : (leashItem ? itemOption(leashItem, 1) : '');

    var targetLeash = harnessItem
      ? (isLargeHarnessSize(harnessSize) ? catalog.leashLxxl : catalog.leashXsm)
      : (leashItem && leashItem.handle === leashLxxlHandle ? catalog.leashLxxl : catalog.leashXsm);

    var products = [];
    var ringInCart = items.some(function (item) {
      return item.handle === 'aggancio-frontale-gratuito' || item.handle === 'aggancio-frontale';
    });

    // Ogni prodotto gia presente nel carrello sparisce dai suggerimenti.
    // Niente seconda pettorina, seconda patch o secondo guinzaglio automatici.
    function isAlreadyInCart(product) {
      return !!product && items.some(function (item) {
        return String(item.product_id) === String(product.id) ||
          (item.handle && item.handle === product.handle);
      });
    }
    function suggest(product) {
      if (product && !isAlreadyInCart(product)) products.push(cloneForCrossell(product, false));
    }

    // Prima la parte mancante del set, poi accessori compatibili.
    if (leashItem && !harnessItem) {
      suggest(catalog.pettorina);
    } else if (harnessItem && !leashItem) {
      suggest(targetLeash);
    } else if (!harnessItem && !leashItem) {
      suggest(catalog.pettorina);
      suggest(targetLeash);
    }

    suggest(catalog.patch);

    // Non vendere il D-Ring se una pettorina lo include gratis.
    if (!ringInCart && !harnessItem) suggest(catalog.frontPaid);

    // Sia il portabustine normale sia quello in omaggio contano come presenti.
    if (!pouchItem) suggest(catalog.portabustine);

    // Nel caso di una sola variante/leash incompleto, proponi solo accessori
    // pertinenti, mai prodotti gia aggiunti ne varianti di taglie incompatibili.
    var seen = {};
    S.products = products.filter(function (product) {
      if (!product || !product.id || !Array.isArray(product.variants) || !product.variants.length) return false;
      if (!product.variants.some(function (v) { return v.available && v.price >= 0; })) return false;
      if (isAlreadyInCart(product)) return false;
      var key = String(product.id);
      if (seen[key]) return false;
      seen[key] = true;
      return true;
    }).slice(0, 4);

    // Il nome di una pettorina già nel carrello è un suggerimento, mai un dato obbligatorio da copiare.
    var suggestedName = harnessItem ? cartDogName(harnessItem) : '';
    if (suggestedName && catalog.patch && S.dogNames[String(catalog.patch.id)] == null) {
      S.dogNames[String(catalog.patch.id)] = suggestedName;
    }

    var sourceVariant = harnessItem ? harnessItem.variant_id : (leashItem ? leashItem.variant_id : 'none');

    S.products.forEach(function (product) {
      var signature = String(sourceVariant) + '|' + String(product.handle) + '|' + canonicalColor(sourceColor) + '|' + norm(sourceSize) + '|' + (product._crossellDuplicate ? 'dup' : 'normal');
      initSelectionsForProduct(product, sourceColor, sourceSize, signature);
    });
  }

  /* ─── CARICA DATI DA window.__BAU_CROSSELL__ ─── */
  function loadData(cart) {
    var data = window.__BAU_CROSSELL__;
    if (!data) {
      S.products = [];
      S.config = null;
      S.catalog = null;
      return false;
    }

    S.config = data;
    S.catalog = data.catalog || null;
    S.currency = data.currency || S.currency;
    S.locale   = data.locale   || S.locale;

    if (S.catalog) {
      resolveProductsFromCart(cart || { items: S.cartItems });
      return true;
    }

    /* Compatibilità con la vecchia configurazione products: [] */
    if (!Array.isArray(data.products)) {
      S.products = [];
      return false;
    }

    S.products = data.products;
    S.products.forEach(function (product) {
      initSelectionsForProduct(product, '', '', 'legacy|' + String(product.id));
    });
    return true;
  }

  /* ─── SLIDER: NAVIGAZIONE IN RANGE (anche dopo refresh AJAX) ─── */
  function maxSlideScroll(track) {
    return track ? Math.max(0, track.scrollWidth - track.clientWidth) : 0;
  }

  function syncSlideArrows() {
    var section = document.getElementById('bau-crossell');
    var track = document.getElementById('bau-crossell-root');
    if (!section || !track) return;
    var nav = section.querySelector('.bcrx__nav');
    var prev = section.querySelector('[data-bcrx-nav="prev"]');
    var next = section.querySelector('[data-bcrx-nav="next"]');
    var limit = maxSlideScroll(track);
    var canScroll = S.products.length > 1 && limit > 2;
    if (nav) nav.hidden = !canScroll;
    if (prev) {
      prev.disabled = !canScroll || track.scrollLeft <= 2;
      prev.setAttribute('aria-disabled', String(prev.disabled));
    }
    if (next) {
      next.disabled = !canScroll || track.scrollLeft >= limit - 2;
      next.setAttribute('aria-disabled', String(next.disabled));
    }
  }

  function navigateSlides(direction) {
    var track = document.getElementById('bau-crossell-root');
    if (!track) return;
    var max = maxSlideScroll(track);
    var cards = Array.prototype.slice.call(track.querySelectorAll('.bcrx__card'));
    if (!cards.length || max < 2) { syncSlideArrows(); return; }

    // Ogni tappa corrisponde all'inizio di una card, inclusa l'ultima
    // (limitata al bordo del contenuto: impossibile scorrere nel vuoto).
    var first = cards[0].offsetLeft;
    var stops = [0];
    cards.forEach(function (card) {
      var stop = Math.min(max, Math.max(0, card.offsetLeft - first));
      if (stop - stops[stops.length - 1] > 2) stops.push(stop);
    });
    if (max - stops[stops.length - 1] > 2) stops.push(max);

    var current = Math.max(0, Math.min(max, track.scrollLeft));
    var target = current;
    if (direction === 'next') {
      target = stops.find(function (stop) { return stop > current + 2; });
      if (target === undefined) target = max;
    } else {
      var earlier = stops.filter(function (stop) { return stop < current - 2; });
      target = earlier.length ? earlier[earlier.length - 1] : 0;
    }
    target = Math.max(0, Math.min(max, target));
    if (Math.abs(target - current) < 2) { syncSlideArrows(); return; }
    track.scrollTo({ left: target, behavior: 'smooth' });
    // Stato dei bottoni sincronizzato anche a fine animazione via scroll.
  }

  /* ─── RENDER ─── */
  function render() {
    var root = document.getElementById('bau-crossell-root');
    if (!root) return;

    var previousScroll = root.scrollLeft || 0;
    var section = document.getElementById('bau-crossell');
    if (!S.products.length) {
      root.innerHTML = '';
      root.scrollLeft = 0;
      if (section) section.hidden = true;
      syncSlideArrows();
      return;
    }

    if (section) section.hidden = false;
    root.innerHTML = S.products.map(renderCard).join('');
    // Dopo un cambio di carrello possono esserci meno card: mai
    // ripristinare una posizione oltre l'ultima card (zona bianca).
    root.scrollLeft = Math.min(previousScroll, maxSlideScroll(root));
    syncSlideArrows();
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(syncSlideArrows);
    }
  }

  function renderCard(product) {
    var variant   = selectedVariant(product);
    var inCart    = !product._crossellDuplicate && variant && isInCart(variant.id);
    var isPending = S.pendingPid === String(product.id);
    var isOpen    = S.openDropdown === String(product.id);
    var hasOpts   = !isDefaultTitle(product);
    var sel       = S.selections[product.id] || {};

    var variantLabel = '';
    if (hasOpts) {
      variantLabel = sel.size ? (sel.color + ' / ' + sel.size) : (sel.color || '');
    }

    var imgSrc  = (variant && variant.image) || product.image || '';
    var imgHtml = imgSrc
      ? '<img src="' + esc(imgSrc) + '" alt="' + esc(product.title) + '" loading="lazy" width="68" height="68">'
      : '<div class="bcrx__img-placeholder"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="28" height="28"><rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg></div>';

    var priceHtml = '';
    if (variant) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceHtml = '<span class="bcrx__price--old">' + fmt(variant.compare_at_price) + '</span>'
                  + '<span class="bcrx__price--current">' + fmt(variant.price) + '</span>';
      } else {
        priceHtml = '<span class="bcrx__price--current">' + fmt(variant.price) + '</span>';
      }
    }

    var personalized = requiresDogName(product);
    var pid = String(product.id);
    var nameValue = S.dogNames[pid] || '';
    var unavailable = !variant || variant.available === false;
    var html = '<div class="bcrx__card' + (personalized ? ' bcrx__card--personalized' : ' bcrx__card--simple') + (inCart ? ' bcrx__card--active' : '') + (isOpen ? ' bcrx__card--dropdown-open' : '') + '" data-product-id="' + product.id + '" data-duplicate="' + (product._crossellDuplicate ? '1' : '0') + '">';
    html += '<div class="bcrx__card-main">';
    html += '<div class="bcrx__card-image">' + imgHtml + '</div>';
    html += '<div class="bcrx__card-info">';
    html += '<p class="bcrx__card-name">' + esc(product.title) + '</p>';
    html += '<div class="bcrx__card-pricing">' + priceHtml + '</div>';
    if (isPending) {
      html += '<button type="button" class="bcrx__add-btn bcrx__add-btn--loading" disabled><span class="bcrx__spinner"></span></button>';
    } else {
      html += '<button type="button" class="bcrx__add-btn' + (inCart ? ' bcrx__add-btn--added' : '') + '" data-action="toggle" data-product-id="' + product.id + '"' + (unavailable ? ' disabled' : '') + '>' + (unavailable ? 'Esaurito' : (inCart ? 'Rimuovi' : 'Aggiungi più Protezione')) + '</button>';
    }
    html += '</div></div>';

    if (personalized) {
      html += '<div class="bcrx__personalization' + (S.nameErrors[pid] ? ' bcrx__personalization--error' : '') + '">';
      html += '<label class="bcrx__personalization-label" for="bcrx-name-' + pid + '">Nome del Cane <span aria-hidden="true">*</span></label>';
      html += '<div class="bcrx__personalization-field"><input type="text" id="bcrx-name-' + pid + '" class="bcrx__dog-name" data-bcrx-name="' + pid + '" autocomplete="off" maxlength="16" value="' + esc(nameValue) + '" placeholder="Inserisci il nome del tuo cane" aria-required="true"' + (S.nameErrors[pid] ? ' aria-invalid="true"' : '') + '></div>';
      html += '<p class="bcrx__name-error"' + (S.nameErrors[pid] ? '' : ' hidden') + '>Inserisci il nome del tuo cane per continuare.</p>';
      html += '</div>';
    }

    if (hasOpts) {
      html += renderTrigger(product.id, variantLabel, isOpen);
      if (isOpen) html += renderDropdown(product);
    }

    html += '</div>'; /* /card */
    return html;
  }

  function renderTrigger(pid, label, isOpen) {
    return '<button type="button"'
      + ' class="bcrx__variant-trigger' + (isOpen ? ' bcrx__variant-trigger--open' : '') + '"'
      + ' data-action="toggle-dropdown" data-product-id="' + pid + '"'
      + ' aria-expanded="' + (isOpen ? 'true' : 'false') + '">'
      + '<span class="bcrx__variant-trigger-text">Variante: <strong>' + esc(label || 'Seleziona') + '</strong></span>'
      + '<svg class="bcrx__variant-trigger-arrow' + (isOpen ? ' bcrx__variant-trigger-arrow--open' : '') + '"'
      + ' width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"'
      + ' stroke-linecap="round" stroke-linejoin="round"><path d="m19.5 8.25-7.5 7.5-7.5-7.5"/></svg>'
      + '</button>';
  }

  /* ── Dropdown gerarchico: colore (immagine+nome+check) → taglie annidate ── */
  function renderDropdown(product) {
    var sel = S.selections[product.id] || {};
    var colors = getColors(product);
    var firstOptionName = norm(product.options && product.options[0] && product.options[0].name || '');
    var firstOptionLabel = (/taglia|size|misura/.test(firstOptionName)) ? 'Taglia' : 'Colore';
    var activeColor = sel.color || (colors[0] ? colors[0].name : '');
    var sizes = getSizesForColor(product, activeColor);

    var colorHtml = colors.map(function (c) {
      var active = c.name === activeColor;
      return '<button type="button" class="bcrx__color-chip' + (active ? ' bcrx__color-chip--selected' : '') + '"' +
        ' data-action="select-color-row" data-product-id="' + product.id + '" data-color="' + esc(c.name) + '">' +
        '<span class="bcrx__color-chip-image"><img src="' + esc(c.image || '') + '" alt="" loading="lazy"></span>' +
        '<span class="bcrx__color-chip-name">' + esc(c.name) + '</span></button>';
    }).join('');

    var sizeHtml = sizes.length > 1 ? '<div class="bcrx__picker-label bcrx__picker-label--size">Taglia</div><div class="bcrx__size-line">' + sizes.map(function (sz) {
      var selected = sel.size === sz.size;
      return '<button type="button" class="bcrx__size-btn' + (selected ? ' bcrx__size-btn--selected' : '') + (sz.available ? '' : ' bcrx__size-btn--disabled') + '"' +
        ' data-action="select-size" data-product-id="' + product.id + '" data-color="' + esc(activeColor) + '" data-size="' + esc(sz.size || '') + '"' +
        (sz.available ? '' : ' disabled') + '>' + esc(sz.size || 'Unica') + '</button>';
    }).join('') + '</div>' : '';

    return '<div class="bcrx__dropdown"><div class="bcrx__dropdown-inner bcrx__picker">' +
      '<div class="bcrx__picker-label">' + firstOptionLabel + '</div><div class="bcrx__color-line">' + colorHtml + '</div>' + sizeHtml +
    '</div></div>';
  }

  /* ─── SECTIONS BUNDLED ─── */
  function getSections() {
    var sections = [];
    try {
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true, detail: { sections: sections }
      }));
    } catch (e) {}
    return sections.length ? sections : null;
  }

  /* ─── AZIONI CARRELLO ─── */
  // Cross-sell AJAX isolato dal vecchio intercettore fetch della scheda prodotto:
  // il nome viene associato solo a pettorine e patch, non a guinzagli/accessori.
  function apiAdd(body) {
    var url = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart/add.js';
    return new Promise(function (resolve, reject) {
      var req = new XMLHttpRequest();
      req.open('POST', url, true);
      req.setRequestHeader('Content-Type', 'application/json');
      req.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
      req.onload = function () {
        var result;
        try { result = JSON.parse(req.responseText); } catch (e) { reject(e); return; }
        if (req.status < 200 || req.status >= 300) { reject(new Error(result.description || result.message || 'Aggiunta non riuscita')); return; }
        resolve(result);
      };
      req.onerror = function () { reject(new Error('Connessione non disponibile')); };
      req.send(JSON.stringify(body));
    });
  }

  function addToCart(pid, variantId) {
    if (S.pendingPid) return;
    S.pendingPid = String(pid);
    updateCardPending(pid, true);

    var sections = getSections();
    var product = productById(pid);
    var named = requiresDogName(product);
    var body = { id: variantId, quantity: 1 };
    if (named) body.properties = { 'Nome del Cane': S.dogNames[String(pid)].trim() };
    if (sections) { body.sections = sections.join(','); body.sections_url = window.location.pathname; }

    apiAdd(body)
    .then(function (addRes) {
      S.pendingPid = null;
      return fetch(window.Shopify.routes.root + 'cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var detail = { baseEvent: 'variant:add', cart: cart };
          if (addRes.sections) { cart.sections = addRes.sections; detail.sections = addRes.sections; }
          syncCart(cart);
          loadData(cart);
          render();
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:change', { bubbles: true, detail: detail })
          );
        });
    })
    .catch(function (err) {
      console.error('[BauCrossell] add:', err);
      S.pendingPid = null;
      updateCardPending(pid, false);
      var card = document.querySelector('#bau-crossell [data-product-id=\"' + pid + '\"]');
      if (card) { var error = card.querySelector('.bcrx__name-error'); if (error) { error.textContent = err.message || 'Non è stato possibile aggiungere il prodotto.'; error.hidden = false; } } 
    });
  }

  function removeFromCart(pid, variantId) {
    if (S.pendingPid) return;
    var key = getLineKey(variantId);
    if (!key) return;

    S.pendingPid = String(pid);
    updateCardPending(pid, true);

    var sections = getSections();
    var body = { id: key, quantity: 0 };
    if (sections) { body.sections = sections.join(','); body.sections_url = window.location.pathname; }

    fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      S.pendingPid = null;
      syncCart(cart);
      loadData(cart);
      render();
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'line-item:change', cart: cart } })
      );
    })
    .catch(function (err) {
      console.error('[BauCrossell] remove:', err);
      S.pendingPid = null;
      updateCardPending(pid, false);
    });
  }

  /* ── Swap variante mentre il prodotto è già nel carrello (FIX v9.0) ── */
  function swapCartVariant(pid, oldVariantId, newVariantId) {
    if (S.pendingPid) return;
    var key = getLineKey(oldVariantId);
    if (!key) return;

    S.pendingPid = String(pid);
    updateCardPending(pid, true);

    var sections    = getSections();
    var removeBody  = { id: key, quantity: 0 };
    if (sections) { removeBody.sections = sections.join(','); removeBody.sections_url = window.location.pathname; }

    fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(removeBody)
    })
    .then(function (r) { return r.json(); })
    .then(function () {
      var addBody = { id: newVariantId, quantity: 1 };
      if (sections) { addBody.sections = sections.join(','); addBody.sections_url = window.location.pathname; }
      return fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(addBody)
      });
    })
    .then(function (r) { return r.json(); })
    .then(function (addRes) {
      S.pendingPid = null;
      return fetch(window.Shopify.routes.root + 'cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var detail = { baseEvent: 'variant:add', cart: cart };
          if (addRes.sections) { cart.sections = addRes.sections; detail.sections = addRes.sections; }
          syncCart(cart);
          loadData(cart);
          render();
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:change', { bubbles: true, detail: detail })
          );
        });
    })
    .catch(function (err) {
      console.error('[BauCrossell] swap:', err);
      S.pendingPid = null;
      updateCardPending(pid, false);
      render();
    });
  }

  function updateCardPending(pid, isPending) {
    var card = document.querySelector('#bau-crossell .bcrx__card[data-product-id="' + pid + '"]');
    if (!card) return;
    var btn = card.querySelector('.bcrx__add-btn');
    if (!btn) return;
    isPending ? btn.classList.add('bcrx__add-btn--loading') : btn.classList.remove('bcrx__add-btn--loading');
  }

  function handleToggle(pid) {
    var product = productById(pid);
    if (!product) return;
    var v = selectedVariant(product);
    if (!v || !v.available) return;
    var isRemovingExisting = !product._crossellDuplicate && isInCart(v.id);
    if (!isRemovingExisting && requiresDogName(product) && !String(S.dogNames[String(pid)] || '').trim()) {
      S.nameErrors[String(pid)] = true;
      render();
      var input = document.getElementById('bcrx-name-' + String(pid));
      if (input) { input.focus(); if (input.scrollIntoView) input.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }
      return;
    }
    if (product._crossellDuplicate) {
      addToCart(pid, v.id);
      return;
    }
    isInCart(v.id) ? removeFromCart(pid, v.id) : addToCart(pid, v.id);
  }

  /* ── Applica una selezione colore/taglia. Se il prodotto è già
     nel carrello con una variante diversa, esegue lo swap. ── */
  function applySelection(pid, color, size) {
    var product = productById(pid);
    if (!product) return;

    S.selections[pid] = { color: color || '', size: size || '' };

    var newVariant = findVariant(product, color, size);
    if (!newVariant) return;

    if (product._crossellDuplicate || requiresDogName(product)) return;

    var existing = S.cartItems.find(function (i) { return String(i.product_id) === String(pid); });
    if (existing && existing.variant_id !== newVariant.id) {
      swapCartVariant(pid, existing.variant_id, newVariant.id);
    }
  }

  /* ─── RENDER-ON-CHANGE: retry robusto (invariato da v8.0) ─── */
  var _renderTimer = null;

  function scheduleRender(cart) {
    if (cart) syncCart(cart);
    if (_renderTimer) { clearTimeout(_renderTimer); _renderTimer = null; }

    var delays  = [50, 100, 200, 300];
    var attempt = 0;

    function tryRender() {
      _renderTimer = null;
      var root = document.getElementById('bau-crossell-root');
      if (!root) {
        if (attempt < delays.length - 1) {
          attempt++;
          _renderTimer = setTimeout(tryRender, delays[attempt]);
        }
        return;
      }
      loadData(cart || { items: S.cartItems });
      render();
    }

    _renderTimer = setTimeout(tryRender, delays[attempt]);
  }

  var _observer = null;

  function startObserver() {
    if (_observer || typeof MutationObserver === 'undefined') return;

    _observer = new MutationObserver(function (mutations) {
      var needsRender = false;
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        for (var j = 0; j < m.addedNodes.length; j++) {
          var node = m.addedNodes[j];
          if (!node || node.nodeType !== 1) continue;
          if (node.id === 'bau-crossell-root'
            || (node.querySelector && node.querySelector('#bau-crossell-root'))) {
            needsRender = true;
            break;
          }
        }
        if (needsRender) break;
      }
      if (needsRender) {
        loadData({ items: S.cartItems });
        render();
      }
    });

    _observer.observe(document.body, { childList: true, subtree: true });
  }

  /* La navigazione segue scroll touch, mouse e cambi di viewport. */
  document.addEventListener('scroll', function (e) {
    if (e.target && e.target.id === 'bau-crossell-root') syncSlideArrows();
  }, true);
  window.addEventListener('resize', syncSlideArrows);

  /* ─── LISTENER cart:change ─── */
  document.documentElement.addEventListener('cart:change', function (e) {
    var d = e && e.detail;
    if (!d) return;
    var cart = d.cart || null;
    scheduleRender(cart);
  });

  document.addEventListener('input', function (event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches('#bau-crossell [data-bcrx-name]')) return;
    var key = String(input.dataset.bcrxName);
    S.dogNames[key] = input.value.slice(0, 16);
    S.nameErrors[key] = false;
    input.removeAttribute('aria-invalid');
    var wrapper = input.closest('.bcrx__personalization');
    if (wrapper) {
      wrapper.classList.remove('bcrx__personalization--error');
      var error = wrapper.querySelector('.bcrx__name-error');
      if (error) error.hidden = true;
    }
  });

  /* ─── EVENT DELEGATION ─── */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var addBtn = t.closest('[data-action="toggle"]');
    if (addBtn && addBtn.closest('#bau-crossell')) {
      e.preventDefault(); e.stopPropagation();
      handleToggle(addBtn.dataset.productId);
      return;
    }

    var navBtn = t.closest('[data-bcrx-nav]');
    if (navBtn && navBtn.closest('#bau-crossell')) {
      e.preventDefault(); e.stopPropagation();
      if (!navBtn.disabled) navigateSlides(navBtn.dataset.bcrxNav);
      return;
    }

    var trig = t.closest('[data-action="toggle-dropdown"]');
    if (trig && trig.closest('#bau-crossell')) {
      e.preventDefault(); e.stopPropagation();
      var pid = String(trig.dataset.productId);
      S.openDropdown = S.openDropdown === pid ? null : pid;
      render();
      return;
    }

    /* Taglia precisa */
    var sizeBtn = t.closest('[data-action="select-size"]');
    if (sizeBtn && sizeBtn.closest('#bau-crossell')) {
      e.preventDefault(); e.stopPropagation();
      if (sizeBtn.disabled) return;
      applySelection(sizeBtn.dataset.productId, sizeBtn.dataset.color, sizeBtn.dataset.size);
      S.openDropdown = null;
      render();
      return;
    }

    /* Colore senza taglie */
    var colorBtn = t.closest('[data-action="select-color"]');
    if (colorBtn && colorBtn.closest('#bau-crossell')) {
      e.preventDefault(); e.stopPropagation();
      applySelection(colorBtn.dataset.productId, colorBtn.dataset.color, null);
      S.openDropdown = null;
      render();
      return;
    }

    /* Colore: aggiorna la variante ma lascia aperto il picker così
       l'utente può scegliere subito anche la taglia sotto. */
    var colorRow = t.closest('[data-action="select-color-row"]');
    if (colorRow && colorRow.closest('#bau-crossell')) {
      e.preventDefault(); e.stopPropagation();
      var pid2     = colorRow.dataset.productId;
      var color2   = colorRow.dataset.color;
      var product2 = productById(pid2);
      if (product2) {
        var currentSel = S.selections[pid2] || {};
        var sizes2 = getSizesForColor(product2, color2);
        var sameSize = sizes2.find(function (sz) { return sz.available && norm(sz.size) === norm(currentSel.size); });
        var first = sameSize || sizes2.find(function (sz) { return sz.available; });
        applySelection(pid2, color2, first ? first.size : null);
      }
      S.openDropdown = String(pid2);
      render();
      return;
    }

    /* Click su altro elemento dentro il dropdown aperto → chiudi */
    if (t.closest('.bcrx__dropdown')) {
      S.openDropdown = null;
      render();
      return;
    }

    /* Click fuori → chiudi dropdown se aperto */
    if (S.openDropdown && !t.closest('#bau-crossell')) {
      S.openDropdown = null;
      render();
    }
  }, true);

  /* ─── INIT ─── */
  function init() {
    startObserver();

    if (!window.__BAU_CROSSELL__) return;

    var rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(rootPath + 'cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        syncCart(cart);
        loadData(cart);
        render();
      })
      .catch(function () {
        loadData({ items: S.cartItems });
        render();
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.BauCrossell = { render: render, state: S };

})();